import React, { useEffect, useMemo, useState } from "react";
import { logix2Api } from "@/api/adminOneClient";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — AdminOne · Opportunity Radar™ (Phase 16, LOGIX-2™).
 * Top-N ranked opportunities per horizon, with domain filter and Oracle Score V2.
 * Strictly additive — does not touch any existing AdminOne page.
 */
const HORIZONS = [
  { id: "today",   label: "Today" },
  { id: "week",    label: "Week" },
  { id: "month",   label: "Month" },
  { id: "quarter", label: "Quarter" },
  { id: "year",    label: "Year" },
];

const DOMAINS = [
  { id: null,      label: "All" },
  { id: "stocks",  label: "Stocks" },
  { id: "crypto",  label: "Crypto" },
  { id: "fx",      label: "FX" },
  { id: "options", label: "Options" },
  { id: "sports",  label: "Sports" },
];

const GRADE_COLOR = {
  Elite:        "#f5c842",
  Exceptional:  "#e8a020",
  Strong:       "#7ac860",
  Moderate:     "#60b8e8",
  Monitor:      "#7a6a4a",
};

export default function AdminOpportunityRadar() {
  const [horizon, setHorizon] = useState("today");
  const [domain, setDomain] = useState(null);
  const [topN, setTopN] = useState(10);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data: r } = await logix2Api.opportunityRadar({ horizon, top_n: topN, domain });
      setData(r);
    } catch (e) {
      setErr(e?.message || "Failed to load Opportunity Radar.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [horizon, domain, topN]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const opportunities = data?.opportunities || [];

  const stats = useMemo(() => {
    if (!opportunities.length) return null;
    const avg = Math.round(opportunities.reduce((a, b) => a + (b.oracle_score || 0), 0) / opportunities.length);
    const elite = opportunities.filter((o) => o.grade === "Elite").length;
    const exceptional = opportunities.filter((o) => o.grade === "Exceptional").length;
    return { avg, elite, exceptional };
  }, [opportunities]);

  return (
    <div className="admin-page" data-testid="admin-opportunity-radar">
      {/* Phase 26C — Mandated Compliance Lock™ banner */}
      <ComplianceLock variant="banner" testid="opportunity-radar-compliance" />
      <header className="admin-page__hd">
        <div>
          <h1 className="admin-page__title">Opportunity Radar™</h1>
          <p className="admin-page__sub">LOGIX-2™ · Top-N opportunities · Oracle Score™ V2 ranked</p>
        </div>
        <div className="admin-page__badges">
          <span className="admin-badge is-on">LOGIX-2™</span>
          <span className="admin-badge is-on">LIVE</span>
        </div>
      </header>

      {/* Controls */}
      <section className="admin-section" data-testid="admin-radar-controls">
        <div className="admin-toolbar">
          <div className="admin-toolbar__group" role="tablist" aria-label="Horizon">
            {HORIZONS.map((h) => (
              <button
                key={h.id}
                type="button"
                className={`admin-pill ${horizon === h.id ? "is-active" : ""}`}
                onClick={() => setHorizon(h.id)}
                data-testid={`admin-radar-horizon-${h.id}`}
              >
                {h.label}
              </button>
            ))}
          </div>
          <div className="admin-toolbar__group" role="tablist" aria-label="Domain">
            {DOMAINS.map((d) => (
              <button
                key={d.label}
                type="button"
                className={`admin-pill ${domain === d.id ? "is-active" : ""}`}
                onClick={() => setDomain(d.id)}
                data-testid={`admin-radar-domain-${d.id || "all"}`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <div className="admin-toolbar__group">
            <label style={{ fontSize: 11, letterSpacing: "0.12em", color: "rgba(212,197,160,.6)", marginRight: 8 }}>TOP</label>
            <select
              value={topN}
              onChange={(e) => setTopN(parseInt(e.target.value, 10))}
              className="admin-select"
              data-testid="admin-radar-topn"
            >
              {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <button
              type="button"
              className="admin-btn admin-btn--ghost"
              onClick={load}
              disabled={loading}
              data-testid="admin-radar-refresh"
            >
              {loading ? "Refreshing…" : "↻ Refresh"}
            </button>
          </div>
        </div>
      </section>

      {/* Stats */}
      {stats && (
        <section className="admin-grid admin-grid--3" data-testid="admin-radar-stats">
          <div className="admin-card">
            <div className="admin-card__label">Average Oracle Score</div>
            <div className="admin-card__value">{stats.avg}</div>
          </div>
          <div className="admin-card">
            <div className="admin-card__label">Elite (95–100)</div>
            <div className="admin-card__value" style={{ color: GRADE_COLOR.Elite }}>{stats.elite}</div>
          </div>
          <div className="admin-card">
            <div className="admin-card__label">Exceptional (90–94)</div>
            <div className="admin-card__value" style={{ color: GRADE_COLOR.Exceptional }}>{stats.exceptional}</div>
          </div>
        </section>
      )}

      {err && <div className="admin-card admin-card--bad" data-testid="admin-radar-error">{err}</div>}

      {/* Opportunities table */}
      <section className="admin-section" data-testid="admin-radar-table">
        <h2 className="admin-section__h">
          Top {data?.count || 0} Opportunities — {horizon.toUpperCase()} {domain ? `· ${domain.toUpperCase()}` : ""}
        </h2>
        <div style={{ overflowX: "auto" }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Symbol</th>
                <th>Name</th>
                <th>Domain</th>
                <th>Oracle Score™</th>
                <th>Grade</th>
                <th>Confidence</th>
                <th>Risk</th>
                <th>Catalyst</th>
              </tr>
            </thead>
            <tbody>
              {opportunities.map((o, i) => (
                <tr key={o.id} data-testid={`admin-radar-row-${i}`}>
                  <td><b>{i + 1}</b></td>
                  <td><b style={{ color: "#f0dfa0" }}>{o.symbol}</b></td>
                  <td>{o.name}</td>
                  <td><span className="admin-tag">{o.domain}</span></td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <b style={{ color: GRADE_COLOR[o.grade] || "#f0dfa0", fontSize: 14 }}>{o.oracle_score}</b>
                      <div style={{
                        flex: 1, height: 6, background: "rgba(7,16,10,.6)", borderRadius: 3, position: "relative", minWidth: 60, maxWidth: 80,
                      }}>
                        <div style={{
                          height: "100%", width: `${o.oracle_score}%`,
                          background: `linear-gradient(90deg, ${GRADE_COLOR[o.grade] || "#e8a020"}, rgba(245,200,66,.6))`,
                          borderRadius: 3,
                        }}/>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="admin-tag" style={{ color: GRADE_COLOR[o.grade], borderColor: GRADE_COLOR[o.grade] }}>
                      {o.grade}
                    </span>
                  </td>
                  <td>{o.confidence}/10</td>
                  <td>
                    <span className={`admin-tag admin-tag--${o.risk_level === "LOW" ? "ok" : o.risk_level === "HIGH" ? "bad" : "warn"}`}>
                      {o.risk_level}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: "rgba(212,197,160,.7)" }}>{o.catalyst}</td>
                </tr>
              ))}
              {opportunities.length === 0 && !loading && (
                <tr><td colSpan={9} style={{ textAlign: "center", padding: 20, color: "rgba(212,197,160,.5)" }}>No opportunities for the selected filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {data?.generated_at && (
          <p style={{ fontSize: 11, color: "rgba(212,197,160,.45)", marginTop: 6 }}>
            Generated at {new Date(data.generated_at).toLocaleString()} · Powered by LOGIX-2™
          </p>
        )}
      </section>
    </div>
  );
}
